import {Component, OnDestroy, OnInit} from '@angular/core';
import {TicketInsuranceMandateRequestsHelperService} from '@lifeislife/lifeislife-components';
import {filter, map, publishReplay, refCount, switchMap, take} from 'rxjs/operators';
import {Observable, Subject, Subscription} from 'rxjs';
import {
  InsuranceCategoryFamily,
  InsuranceCategoryService,
  SigningPackageService,
  SimplePagination,
  SimplePaginationUtils,
  TicketInsuranceMandateRequest,
  ValidationResult,
} from '@lifeislife/lifeislife-domain';
import {CustomerContextService} from '../service/customer-context-service';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {CustomerInsuranceMandateRequestHelperService} from '../service/customer-insurance-mandate-request-helper.service';
import {ShellMessageService} from '../service/shell-message-service';
import {NavigationService} from '../service/navigation-service';
import {PendingTicketDraftService} from '../service/pending-ticket-draft.service';
import {OfflineInsuranceMandateRequest} from './offline-insurance-mandate-request';

@Component({
  selector: 'cf-mandate-route',
  templateUrl: './mandate-route.component.html',
  styleUrls: ['./mandate-route.component.scss'],
  providers: [TicketInsuranceMandateRequestsHelperService],
})
export class MandateRouteComponent implements OnInit, OnDestroy {

  subscription: Subscription;
  ticketRequest: OfflineInsuranceMandateRequest | null;

  successSignhereLink: string | null;

  committing$: Observable<boolean>;
  pollingPackage: boolean;

  nextDraftMandateRequest$: Observable<TicketInsuranceMandateRequest | null>;
  nextDraftMandateRequestValidationResults$: Observable<ValidationResult<TicketInsuranceMandateRequest>>;

  private syncChangesSource$ = new Subject<boolean>();

  constructor(
    private customerInsuranceMandateRequestHelperService: CustomerInsuranceMandateRequestHelperService,
    private ticketInsuranceMandateRequestsHelperService: TicketInsuranceMandateRequestsHelperService,
    private signingPackageService: SigningPackageService,
    private pendingDraftService: PendingTicketDraftService,
    private customerContextService: CustomerContextService,
    private activatedRoute: ActivatedRoute,
    private shellMessageService: ShellMessageService,
    private navigationService: NavigationService,
    private insuranceCategoryService: InsuranceCategoryService,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.subscription = new Subscription();
    this.nextDraftMandateRequest$ = this.customerInsuranceMandateRequestHelperService.nextDraftMandateRequest$;
    this.nextDraftMandateRequestValidationResults$ = this.customerInsuranceMandateRequestHelperService.nextDraftMandateRequestValidationResults$;


    const paramsSubscription = this.activatedRoute.paramMap.subscribe(p => {
      this.loadCategoryFromParams(p);
    });
    this.subscription.add(paramsSubscription);

    // Create a new draft
    this.customerInsuranceMandateRequestHelperService.createOfflineRequest();
    this.ticketRequest = this.customerInsuranceMandateRequestHelperService.editingTicket$.getValue();
    this.committing$ = this.customerInsuranceMandateRequestHelperService.committingDraft$;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.customerInsuranceMandateRequestHelperService.cancelOfflineRequest();
  }

  onCommitDraftClick() {
    this.successSignhereLink = null;
    this.pollingPackage = false;

    const loggedContactRef = this.customerContextService.getActiveContactRef();
    this.customerInsuranceMandateRequestHelperService.commitDraft$().pipe(
      // This request normally returns once everything is uploaded
      switchMap(t => {
        this.pollingPackage = true;
        return this.ticketInsuranceMandateRequestsHelperService.pollForTicketSigningPackage$(t);
      }),
      map(s => this.signingPackageService.getSignerSignUrl(s, loggedContactRef)),
    ).subscribe((link) => {
        this.pollingPackage = false;
        this.successSignhereLink = link;
        // We wait a bit more before redirecting
        this.navigationService.navigateToUrl(link, 3000);
      },
      error => {
        this.successSignhereLink = null;
        this.shellMessageService.addError(`Impossible d'envoyer la demande`, error);
      });
  }

  onGoSignClick() {
    this.navigationService.navigateToUrl(this.successSignhereLink, 0);
  }


  onEditExistingRequestClick(request: TicketInsuranceMandateRequest) {
    this.customerInsuranceMandateRequestHelperService.setNextDraftRequest(request);
  }

  onDeleteExistingRequestClick(request: TicketInsuranceMandateRequest) {
    this.customerInsuranceMandateRequestHelperService.cancelDraftRequest(request);
  }

  onSaveNextRequestClick() {
    this.customerInsuranceMandateRequestHelperService.addNextDraftRequest();
  }

  onCancelNextRequestCLick() {
    this.customerInsuranceMandateRequestHelperService.cancelNextDraftRequest();
  }

  onAddAnotherRequestClick() {
    this.customerInsuranceMandateRequestHelperService.addNextDraftRequest();
  }

  onAddNewRequestClick() {
    this.customerInsuranceMandateRequestHelperService.addNextDraftRequest();
  }

  onNextRequestChange(value: TicketInsuranceMandateRequest) {
    if (value.providerCategoryName != null && value.providerCategoryName.length === 0) {
      value.providerCategoryName = null;
    }
    this.customerInsuranceMandateRequestHelperService.nextDraftMandateRequest$.next(value);
  }

  onDiscardDraftClick() {
    this.customerInsuranceMandateRequestHelperService.cancelOfflineRequest();
    this.router.navigate(['/products']);
  }

  private loadCategoryFromParams(params: ParamMap) {
    const familyLabelParam = params.get('family');
    const categoryIdParam = params.get('categoryId');
    const parsedCategoryId = parseInt(categoryIdParam, 10);

    this.customerInsuranceMandateRequestHelperService.nextDraftMandateRequest$.pipe(
      filter(m => m != null),
      take(1),
    ).subscribe(mandateRequerst => {
      if (categoryIdParam && !isNaN(parsedCategoryId)) {
        mandateRequerst.insuranceCategoryRef = {id: parsedCategoryId};
      } else if (familyLabelParam) {
        this.insuranceCategoryService.searchInsuranceCategorys$({
          anyFamily: [familyLabelParam as InsuranceCategoryFamily],
        }, SimplePaginationUtils.newPagination(1))
          .subscribe(results => {
            if (results.count > 0) {
              mandateRequerst.insuranceCategoryRef = {id: results.list[0].id};
            }
          });
      }

      this.onNextRequestChange(mandateRequerst);
    });

  }

}
